import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import LineAmChart from './LineAmChart';
import HistogramAmChart from './HistogramAmChart';
import Grid from '@mui/material/Grid';
import { StyledSkeleton } from './styles';
import { Checkbox, FormControlLabel } from '@mui/material';
import ReactHtmlParser from 'react-html-parser';
import { useState, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { COLORS as COLOR } from '../../styles/constants';

const selectSimulationData = state => state.modelData.simulationData;
const makeSelectSimulationDataById = simulationNumber =>
	createSelector([selectSimulationData], allData =>
		allData.find(data => data.simulationId === simulationNumber.toString())
	);

const Charts = ({ simulationNumber, chartDescription }) => {
	const { t } = useTranslation();
	const loadingSimulation = useSelector(state => state.modelData.loading.includes(simulationNumber));
	const displayInput = useSelector(state => state.modelData.displayInput);
	const tableData = useSelector(state => state.modelData.tableData);
	const [undoClamping, setUndoClamping] = useState(false);
	const clampingMappingNames = [];
	const selectSimulationDataById = useMemo(() => makeSelectSimulationDataById(simulationNumber), [simulationNumber]);
	const simulationData = useSelector(selectSimulationDataById);
	const simulations = useSelector(state => state.designDefinition.partDefinition.simulations);
	const simulationName = simulations[simulationNumber].heading;

	chartDescription.forEach(chart => {
		chart.axis.forEach(axe => {
			if (
				axe.clampMaxRating &&
				axe.clampMaxRating.mappingName &&
				!clampingMappingNames.includes(axe.clampMaxRating.mappingName)
			) {
				clampingMappingNames.push(axe.clampMaxRating.mappingName);
			}
		});
	});

	const displayInputData = useMemo(() => {
		return displayInput && displayInput[simulationNumber] ? displayInput[simulationNumber] : null;
	}, [displayInput, simulationNumber]);

	if (loadingSimulation) {
		return (
			<Grid item container justify="space-between" sx={{ mb: 13 }}>
				{chartDescription.map((chart, index) => (
					<Grid item xs key={`skeleton-${index}`}>
						<StyledSkeleton
							variant="rectangular"
							sx={{
								mt: 10,
								ml: 8,
								mb: 2,
								borderRadius: 3
							}}
						/>
					</Grid>
				))}
			</Grid>
		);
	}

	const handleCheck = () => {
		setUndoClamping(!undoClamping);
	};

	let clampNote = ReactHtmlParser(
		t('components.charts.clampNotePart1') +
			(tableData !== null && !_.isEmpty(tableData)
				? ' ( ' +
				  clampingMappingNames
						.map(name => {
							return tableData[name].heading;
						})
						.join(', ') +
				  ' ), '
				: '')
	);

	if (simulationData && simulationData.errorMessage) {
		return (
			<Grid item xs key="error">
				<div dangerouslySetInnerHTML={{ __html: simulationData.errorMessage }}></div>
			</Grid>
		);
	}

	return (
		<Grid item container direction="column" justifyContent="center" alignItems="center">
			{clampingMappingNames.length !== 0 && (
				<Grid item xs key={`note`} sx={{ mt: '30px' }}>
					<span>{clampNote}</span>
					<span style={{ color: COLOR.PRIMARY }}>{t('components.charts.clampNotePart2')}</span>
				</Grid>
			)}
			{clampingMappingNames.length !== 0 && (
				<Grid item xs key={`checkBox`} sx={{ mb: '-30px' }}>
					<FormControlLabel
						control={<Checkbox onChange={handleCheck} checked={undoClamping} />}
						label={t('components.charts.checkBox')}
						labelPlacement="start"
					/>
				</Grid>
			)}
			<Grid item container justify="space-between" sx={{ mt: '50px', mb: '50px' }}>
				{chartDescription.map((chart, index) => {
					if (simulationData && simulationData.chartsData) {
						if ((chart.chartType === 'LineChart' || chart.chartType === 'ComposedChart') && chart.visible) {
							return (
								<Grid item xs key={`lineChart-${index}`}>
									<LineAmChart
										data={simulationData.chartsData[index]}
										chartDescription={chart}
										displayInput={displayInputData}
										tableData={tableData}
										undoClamChecked={undoClamping}
										simulationName={simulationName}
									/>
								</Grid>
							);
						} else if (chart.chartType === 'Histogram') {
							return (
								<Grid item xs key={`histogram-${index}`}>
									<HistogramAmChart
										data={simulationData.chartsData[index]}
										chartDescription={chart}
										displayInput={displayInputData}
										simulationName={simulationName}
									/>
								</Grid>
							);
						}
					}
					return null;
				})}
			</Grid>
		</Grid>
	);
};

export default Charts;
